import { useCallback, useMemo, useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Slider as MuiSlider, Typography } from '@mui/material';
import { ExpandLess, ExpandMore, MoreVert } from '@mui/icons-material';
import {
  formatNumberWithAbbrev,
  formatNumberWithComma,
} from '../../ChartOptions/Format/NumberFormatter';
import { FormatSettings } from '../../../redux/Parameters/ParameterInterface';
import {
  toggleAxesEdited,
  updateAxesQueryParam,
} from '../../../redux/ChartPoperties/ChartPropertiesActions';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { useRef, useEffect } from 'react';
import {
  setParameterData,
  setParameterIntoFilters,
} from '../../../redux/Parameters/ParametersAction';
import { chartFilterGroupEdited } from '../../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import { updateCalculation } from '../../../redux/Calculations/CalculationsActions';
import { ParameterProps } from '../ParameterContainer';
import { callQueryAPI } from '../../../redux/ChartPoperties/ChartControlsActions';
import { callQueryAPIDashboard } from '../../../redux/TabTile/TabTileActionsAndMultipleDispatches';
import { AxisType } from '../../CommonFunctions/aliases';

const Slider = ({
  parameter,
  handleEditParameter,
  propKey,
  editMode,
  showDash,
  backgroundColor,
  color,
}: ParameterProps) => {
  const { chartProperties, calculations } = useSelector((state: any) => {
    return {
      chartProperties: state.chartProperties,
      calculations: state.calculations,
    };
  });
  const dispatch = useDispatch();

  const currentChartAxesRef = useRef<any[]>([]);
  const currentChatFieldRef = useRef<any>(null);
  const currentChartAxesNameRef = useRef<any>(null);
  const itemIndexRef = useRef<any>(null);
  const isFirstRender = useRef(true);

  const [sliderValue, setSliderValue] = useState(() => {
    const minValue = Number(parameter.minValue);
    const maxValue = Number(parameter.maxValue);
    if (
      parameter.userSelection !== '' &&
      parameter.userSelection !== undefined &&
      parameter.userSelection !== null
    )
      return parameter.userSelection;

    const userValue = Number(parameter.userSelection);
    if (userValue >= minValue && userValue <= maxValue) {
      return userValue;
    }

    return minValue;
  });
  const [savedInputValue, setSavedInputValue] = useState<number>();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expanded, setExpanded] = useState(true);

  const currentChartField = chartProperties.properties[propKey]?.chartAxes[0]?.fields?.find(
    (field: any) => field.parameterId === parameter.parameterId,
  );

  const open = Boolean(anchorEl);
  const formatOptions: FormatSettings = parameter.formatOptions;

  const formatValue = (value: any) => {
    if (formatOptions.enableRounding) {
      value = Number(value).toFixed(formatOptions.roundingDigits);
    }

    if (formatOptions.numberSeparator === 'Abbrev' && formatOptions.formatValue !== 'Percent') {
      value = formatNumberWithAbbrev(value, formatOptions.roundingDigits);
    }
    if (formatOptions.formatValue === 'Percent') {
      value = value / 100;
      value = `${value}%`;
    }

    if (formatOptions.currencySymbol.length > 0) {
      value = `${formatOptions.currencySymbol}${value}`;
    }

    if (formatOptions.numberSeparator === 'Comma') {
      value = formatNumberWithComma(value);
    }
    return value;
  };

  const minFormattedValue = formatValue(parameter.minValue);
  const maxFormattedValue = formatValue(parameter.maxValue);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const callQueryAPIFilter = () => {
    let callAPIDashboard = false;
    chartProperties.propList[1].forEach((tabTileId: string) => {
      chartProperties.properties[tabTileId].chartAxes[0].fields.some((field: any) => {
        if (field.parameterId === parameter.parameterId) {
          dispatch(callQueryAPI(tabTileId, true));
          callAPIDashboard = true;
        }
      });
      let measureAxes = chartProperties.properties[tabTileId].chartAxes.filter((axis: any) =>
        [AxisType.Measure, AxisType.X, AxisType.Y].includes(axis.name),
      );
      measureAxes.forEach((axes: any) => {
        axes.fields.some((field: any) => {
          if (field.override) {
            field.override[0].fields.some((FilterField: any) => {
              if (FilterField.parameterId === parameter.parameterId) {
                dispatch(callQueryAPI(tabTileId, true));
                callAPIDashboard = true;
              }
            });
          }
        });
      });
    });
    if (showDash && callAPIDashboard) {
      dispatch(callQueryAPIDashboard(true));
    }
  };

  const updateUserFilterItem = (
    propKey: string,
    bIndex: number,
    itemIndex: number,
    field: any,
    axesName: string,
  ) => {
    dispatch(updateAxesQueryParam(propKey, bIndex, itemIndex, field, axesName));
  };

  const callAPI = (uid: string | undefined) => {
    let callAPIDashboard = false;
    Object.entries(chartProperties.properties).forEach(([tabTileId, prop]: [string, any]) => {
      prop.chartAxes?.forEach((axis: any) => {
        axis.fields?.forEach((field: any) => {
          if (field.SavedCalculationUUID === uid) {
            dispatch(callQueryAPI(tabTileId, true));
            callAPIDashboard = true;
          }
        });
      });
    });
    if (showDash && callAPIDashboard) {
      dispatch(callQueryAPIDashboard(true));
    }
  };

  const debouncedUpdateFilter = useCallback(
    debounce((value: number) => {
      if (value === savedInputValue) return;
      setSavedInputValue(value);

      // if (currentChatFieldRef.current && currentChatFieldRef.current.isParameterized) {
      //   Object.defineProperty(currentChatFieldRef.current, 'exprInput', {
      //     value: value,
      //     writable: true,
      //     enumerable: true,
      //     configurable: true,
      //   });
      //   currentChatFieldRef.current.exprInput = value;

      //   updateUserFilterItem(
      //     propKey,
      //     0,
      //     itemIndexRef.current,
      //     currentChatFieldRef.current,
      //     currentChartAxesNameRef.current,
      //   );
      // }
      Array.isArray(parameter.chartFilter) &&
        parameter.chartFilter.forEach((cf: any) => {
          if (!cf.currentChartAxesName?.includes('chartAxes_')) {
            if (cf.propKey) {
              const clonedField = {
                ...chartProperties.properties[cf.propKey].chartAxes[0].fields[cf.itemIndex],
              };
              Object.defineProperty(clonedField, 'exprInput', {
                value: value,
                writable: true,
                enumerable: true,
                configurable: true,
              });
              clonedField.exprInput = value;
              updateUserFilterItem(
                cf.propKey,
                0,
                cf.itemIndex,
                clonedField,
                cf.currentChartAxesName,
              );
            } else if (cf.itemIndex >= 0 && cf.currentChartAxesName) {
              chartProperties.propList[1].forEach((tabTileId: string) => {
                chartProperties.properties[tabTileId].chartAxes[0].fields.some((field: any) => {
                  if (field.parameterId === parameter.parameterId) {
                    dispatch(
                      setParameterIntoFilters(
                        tabTileId,
                        parameter.parameterId,
                        field.uId,
                        cf.itemIndex,
                        cf.currentChartAxesName,
                      ),
                    );

                    const clonedField = {
                      ...chartProperties.properties[tabTileId].chartAxes[0].fields[cf.itemIndex],
                    };
                    Object.defineProperty(clonedField, 'exprInput', {
                      value: value,
                      writable: true,
                      enumerable: true,
                      configurable: true,
                    });
                    clonedField.exprInput = value;
                    updateUserFilterItem(
                      tabTileId,
                      0,
                      cf.itemIndex,
                      clonedField,
                      cf.currentChartAxesName,
                    );
                  }
                });
              });
            }
          }
        }),
        callQueryAPIFilter();

      if (parameter.calculationId.length > 0) {
        const targetCalculation = calculations.savedCalculations.filter(
          (cal: any) => cal.uuid === parameter.calculationId.find((id: any) => id === cal.uuid),
        );
        targetCalculation.map((calculation: any) => {
          const targetFlowNames = Object.keys(calculation.calculationInfo.flows);
          targetFlowNames.map((flowName: any) => {
            const targetFlow = calculation.calculationInfo.flows[flowName][0];
            const conditionFilters = calculation.calculationInfo.conditionFilters;
            if (targetFlow.flow === 'IfElse') {
              for (const conditionFilter in conditionFilters) {
                let newConditions = conditionFilters[conditionFilter][0];
                newConditions.conditions.forEach((filter: any) => {
                  if (filter.parameterId.length > 0) {
                    filter.parameterId.map((parameterId: string, index: number) => {
                      if (parameterId === parameter.parameterId) {
                        filter.rightOperand[index] = value.toString();
                      }
                    });
                  }
                });
              }
              dispatch(toggleAxesEdited(propKey, true));
              dispatch(chartFilterGroupEdited(true));
              dispatch(updateCalculation(calculation));
            } else {
              let source = targetFlow.source;
              let input;
              if (value <= 0) {
                if (targetFlow.flow === 'division') input = 1;
                else input = 0;
              } else {
                input = value;
              }
              source = source.map((source: any, index: number) => {
                const isParameterized = targetFlow.parameter.find(
                  (param: any) =>
                    param.parameterId === parameter.parameterId && param.index === index,
                );
                if (isParameterized) {
                  return input;
                }
                return source;
              });
              targetFlow.source = source;
              calculation.calculationInfo.flows[flowName][0] = targetFlow;
              dispatch(toggleAxesEdited(propKey, true));
              dispatch(chartFilterGroupEdited(true));
              dispatch(updateCalculation(calculation));
            }
            callAPI(calculation.uuid);
          });
        });
      }

      parameter?.chartFilter?.forEach((filter: any) => {
        if (filter?.currentChartAxesName?.includes('chartAxes_')) {
          const currentChartAxesName_ = filter?.currentChartAxesName.split('_')[1];
          if (filter.propKey) {
            const chartAxes = chartProperties.properties[propKey]?.chartAxes;
            const chartFilter = chartAxes[chartAxes.length - 1].fields;
            chartFilter?.forEach((field: any, index: number) => {
              if (currentChartAxesName_ === field.enableOverrideForUID) {
                return;
              }
              if (field.uId === currentChartAxesName_) {
                const override_ = field.override;
                if (Array.isArray(override_) && override_.length > 0) {
                  override_[0].fields.forEach((field_: any, index_: number) => {
                    if (field_.uId === filter.chartFilterId) {
                      field_.exprInput = value;
                      field_.userSelection = [value];
                      field.override = override_;
                      updateUserFilterItem(
                        propKey,
                        chartAxes.length - 1,
                        index,
                        field,
                        'chartAxes',
                      );
                      callQueryAPIFilter();
                    }
                  });
                }
              }
            });
          } else {
          }
        }
      });
    }, 500),
    [propKey],
  );

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    const step =
      Number(parameter.stepValue) || (parameter.columns?.dataType === 'decimal' ? 0.1 : 1);
    const decimalPlaces = step.toString().split('.')[1]?.length || 0;

    const adjustedValue = Number(
      (Math.round((newValue as number) / step) * step).toFixed(decimalPlaces),
    );

    const clampedValue = Math.min(
      Math.max(adjustedValue, Number(parameter.minValue)),
      Number(parameter.maxValue),
    );

    setSliderValue(clampedValue);
    dispatch(setParameterData(parameter.parameterId, clampedValue));
    debouncedUpdateFilter(clampedValue);
  };

  useEffect(() => {
    currentChartAxesRef.current = chartProperties.properties[propKey]?.chartAxes[0]?.fields || [];

    // Find the field where any chartFilter in parameter.chartFilter matches filter.uId
    currentChatFieldRef.current = currentChartAxesRef.current.find(
      (filter: any) =>
        Array.isArray(parameter.chartFilter) &&
        parameter.chartFilter.some((cf: any) => {
          if (cf.chartFilterId === filter.uId) {
            currentChartAxesNameRef.current = cf.currentChartAxesName;
            itemIndexRef.current = cf.itemIndex;
            return cf.chartFilterId === filter.uId;
          }
        }),
    );
  }, [chartProperties.properties[propKey]?.chartAxes[0]?.fields, parameter.chartFilter, propKey]);

  // if the parameter had any default value then it will update the Graph
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (currentChatFieldRef.current && currentChatFieldRef.current.isParameterized) {
      Object.defineProperty(currentChatFieldRef.current, 'exprInput', {
        value: sliderValue,
        writable: true,
        enumerable: true,
        configurable: true,
      });
      currentChatFieldRef.current.exprInput = sliderValue;

      updateUserFilterItem(
        propKey,
        0,
        itemIndexRef.current,
        currentChatFieldRef.current,
        currentChartAxesNameRef.current,
      );
      callQueryAPIFilter();
    }

    if (parameter.calculationId.length > 0) {
      const targetCalculation = calculations.savedCalculations.filter(
        (cal: any) => cal.uuid === parameter.calculationId.find((id: any) => id === cal.uuid),
      );
      targetCalculation.map((calculation: any) => {
        const targetFlowNames = Object.keys(calculation.calculationInfo.flows);
        targetFlowNames.map((flowName: any) => {
          const targetFlow = calculation.calculationInfo.flows[flowName][0];
          const conditionFilters = calculation.calculationInfo.conditionFilters;
          if (targetFlow.flow === 'IfElse') {
            for (const conditionFilter in conditionFilters) {
              let newConditions = conditionFilters[conditionFilter][0];
              newConditions.conditions.forEach((filter: any) => {
                if (filter.parameterId.length > 0) {
                  filter.parameterId.map((parameterId: string, index: number) => {
                    if (parameterId === parameter.parameterId) {
                      filter.rightOperand[index] = sliderValue.toString();
                    }
                  });
                }
              });
            }
            dispatch(toggleAxesEdited(propKey, true));
            dispatch(chartFilterGroupEdited(true));
            dispatch(updateCalculation(calculation));
          } else {
            let source = targetFlow.source;
            let input;
            if (sliderValue <= 0) {
              if (targetFlow.flow === 'division') input = 1;
              else input = 0;
            } else {
              input = sliderValue;
            }
            source = source.map((source: any, index: number) => {
              const isParameterized = targetFlow.parameter.find(
                (param: any) =>
                  param.parameterId === parameter.parameterId && param.index === index,
              );
              if (isParameterized) {
                return input;
              }
              return source;
            });
            targetFlow.source = source;
            calculation.calculationInfo.flows[flowName][0] = targetFlow;
            dispatch(toggleAxesEdited(propKey, true));
            dispatch(chartFilterGroupEdited(true));
            dispatch(updateCalculation(calculation));
          }
        });
      });
    }

    parameter?.chartFilter?.forEach((filter: any) => {
      if (filter?.currentChartAxesName?.includes('chartAxes_')) {
        const currentChartAxesName_ = filter?.currentChartAxesName.split('_')[1];
        const chartAxes = chartProperties.properties[propKey]?.chartAxes;
        const chartFilter = chartAxes[chartAxes.length - 1].fields;
        chartFilter?.forEach((field: any, index: number) => {
          if (currentChartAxesName_ === field.enableOverrideForUID) {
            return;
          }
          if (field.uId === currentChartAxesName_) {
            const override_ = field.override;
            if (Array.isArray(override_) && override_.length > 0) {
              override_[0].fields.forEach((field_: any, index_: number) => {
                if (field_.uId === filter.chartFilterId) {
                  field_.exprInput = sliderValue;
                  field_.userSelection = [sliderValue];
                  field.override = override_;
                  updateUserFilterItem(propKey, chartAxes.length - 1, index, field, 'chartAxes');
                  callQueryAPIFilter();
                }
              });
            }
          }
        });
      }
    });
  }, [
    parameter.chartFilter,
    // currentChatFieldRef.current,
    // propKey,
    // currentChartField?.parameterId
  ]);

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography
            variant="body2"
            sx={{
              mb: 0.5,
              fontWeight: 500,
              transform: 'capitalize',
              color: color,
              textAlign: 'start',
            }}
          >
            {parameter?.name}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: -1,
            transition: 'all 0.3s ease-in-out',
          }}
        >
          {editMode && (
            <IconButton
              size="small"
              id="basic-button"
              aria-controls={open ? 'basic-menu' : undefined}
              sx={{
                color: color,
              }}
              onClick={handleClick}
            >
              <MoreVert />
            </IconButton>
          )}
          <IconButton
            size="small"
            sx={{
              mr: '0.4rem',
              color: color,
            }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem dense onClick={() => handleEditParameter(parameter.parameterId)}>
          Remove
        </MenuItem>
      </Menu>

      {expanded && (
        <>
          <Typography
            variant="caption"
            sx={{
              color: color,
              fontWeight: 500,
              display: 'block',
              mb: 1.2,
              textAlign: 'center',
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            {formatValue(sliderValue)}
          </Typography>

          <MuiSlider
            value={Number(sliderValue)}
            onChange={handleSliderChange}
            min={Number(parameter.minValue)}
            max={Number(parameter.maxValue)}
            step={
              Number(parameter.stepValue) || (parameter.columns?.dataType === 'decimal' ? 0.1 : 1)
            }
            valueLabelDisplay="auto"
            marks={[
              { value: Number(parameter.minValue), label: minFormattedValue },
              { value: Number(parameter.maxValue), label: maxFormattedValue },
            ]}
            sx={{
              color: color || '#787878',
              width: '93%',
              '& .MuiSlider-thumb': {
                width: '0.8rem',
                height: '0.8rem',
                color: color,
                '&:hover': {
                  boxShadow: '0px 0px 0px 8px rgba(0, 0, 0, 0.16) !important',
                },
                '&:active': {
                  boxShadow: '0px 0px 0px 8px rgba(0, 0, 0, 0.16) !important',
                },
              },
              '& .MuiSlider-markLabel': {
                color: color,
                '&[data-index="0"]': {
                  transform: 'translateX(0%)',
                },
                '&[data-index="1"]': {
                  transform: 'translateX(-100%)',
                },
                marginTop: '-0.2rem',
              },
              '& .MuiSlider-track': {
                color: '#939393',
              },
              '& .Mui-focusVisible': {
                boxShadow: '0px 0px 0px 8px rgba(0, 0, 0, 0.16) !important',
              },
            }}
          />
        </>
      )}
    </Box>
  );
};

export default Slider;
